/**
 * Converts the date string format YYYY-MM-DD (as per OpenAPI specs) to a Date object.
 * @link (https://swagger.io/docs/specification/data-models/data-types/#string)
 * @param {string | undefined} isoDate - Full date formatted string in the form of "YYYY-MM-DD", as per ISO 8601
 * @return {Date | null} Corresponding Date object, or null (if date string format does not match YYYY-MM-DD), timezone at UTC+0
 *
 * @example
 *     parseIsoDate('2024-04-01') // OK
 *     parseIsoDate('13-1-1') // Not OK
 */
export const parseIsoDate = (isoDate: string | undefined): Date | null => {
  const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/; // YYYY-MM-DD
  if (!isoDate || !isoDateRegex.test(isoDate)) {
    return null;
  }

  const parsedDate = Date.parse(isoDate);
  if (Number.isNaN(parsedDate)) {
    return null;
  }

  return new Date(parsedDate);
};
const formatterCache = new Map<string, Intl.DateTimeFormat>();
const MAX_CACHE_SIZE = 100;

function deepSortObjectKeys(obj: unknown): unknown {
  if (typeof obj !== 'object' || obj === null) return obj;
  if (Array.isArray(obj)) return obj.map(deepSortObjectKeys);

  const o = obj as Record<string, unknown>;
  return Object.keys(o)
    .sort()
    .reduce(
      (acc, key) => {
        if (key === '__proto__' || key === 'constructor') {
          return acc;
        }
        // eslint-disable-next-line security/detect-object-injection
        acc[key] = deepSortObjectKeys(o[key]);
        return acc;
      },
      {} as Record<string, unknown>,
    );
}

export function getDateTimeFormatter(
  locales: string | string[],
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const key = JSON.stringify({locales, options: deepSortObjectKeys(options)});
  const cached = formatterCache.get(key);
  if (cached) {
    return cached;
  }
  if (formatterCache.size >= MAX_CACHE_SIZE) {
    const firstKey = formatterCache.keys().next().value;
    if (firstKey !== undefined) {
      formatterCache.delete(firstKey);
    }
  }
  const formatter = new Intl.DateTimeFormat(locales, options);
  formatterCache.set(key, formatter);
  return formatter;
}
